import { useConnection, useWallet } from '@solana/wallet-adapter-react'
import {
  Keypair,
  LAMPORTS_PER_SOL,
  PublicKey,
  Signer,
  Transaction,
  TransactionInstruction,
  TransactionSignature,
} from '@solana/web3.js'
import * as spl from '@solana/spl-token'
import { FC, useCallback, useState } from 'react'
import {
  BANANA_MINT,
  KONGS_HOARD,
  MARINADE_PROGRAM_ID,
  MARINADE_STATE_ID,
  MSOL_LEG,
  MSOL_MINT,
  MSOL_TREASURY,
  PROGRAM_STATE_ACCOUNT,
  PROGRAM_VAULT_SEED,
  RAD_ID,
  SOL_LEG,
  SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
} from '../../utils/constants'
import { Button, CircularProgress } from '@mui/material'
import { struct, u8 } from '@solana/buffer-layout'
import { useDispatch } from 'react-redux'
import { useNotify } from '../../utils/notify'
import { setScanForNewLoans } from '../../redux/modules/loans'
import { TOKEN_PROGRAM_ID } from '@solana/spl-token'

interface CloseLoanProps {
  loanAccount: PublicKey
  loanAmount: number
  buttonSize?: 'small' | 'large' | 'medium' | undefined
  text?: string
}

async function findAssociatedTokenAddress(
  walletAddress: PublicKey,
  tokenMintAddress: PublicKey
): Promise<PublicKey> {
  return (
    await PublicKey.findProgramAddress(
      [
        walletAddress.toBuffer(),
        TOKEN_PROGRAM_ID.toBuffer(),
        tokenMintAddress.toBuffer(),
      ],
      SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID
    )
  )[0]
}

export const CloseLoan: FC<CloseLoanProps> = (props) => {
  const { connection } = useConnection()
  const { publicKey, sendTransaction } = useWallet()
  const [loading, setLoading] = useState(false)
  const dispatch = useDispatch()
  const notify = useNotify()

  const onClick = useCallback(async () => {
    if (!publicKey) {
      notify('error', 'Wallet not connected!')
      return
    }

    let signature: TransactionSignature = ''
    try {
      setLoading(true)
      const owner = publicKey

      const user_banana_transfer_authority = new Keypair()

      // const user_banana_account_seed = 'USER_BANANA_ACCOUNT'
      // const user_banana_account_derived_pubKey = await PublicKey.createWithSeed(
      //   props.loanAccount,
      //   user_banana_account_seed,
      //   TOKEN_PROGRAM_ID
      // )
      const bananaAtaAddress = await findAssociatedTokenAddress(
        owner,
        BANANA_MINT
      )

      const program_vault_derived_pubKey = await PublicKey.createWithSeed(
        props.loanAccount,
        PROGRAM_VAULT_SEED,
        TOKEN_PROGRAM_ID
      )
      const programVaultAuthority = await PublicKey.findProgramAddress(
        [props.loanAccount.toBuffer(), program_vault_derived_pubKey.toBuffer()],
        RAD_ID
      )

      let dataLayout = struct([u8('instruction')])
      const data = Buffer.alloc(dataLayout.span)
      dataLayout.encode(
        {
          instruction: 1,
        },
        data
      )

      const transaction = new Transaction().add(
        // Approve the transfer of the stable coin
        spl.Token.createApproveInstruction(
          TOKEN_PROGRAM_ID,
          bananaAtaAddress,
          user_banana_transfer_authority.publicKey,
          owner,
          [],
          props.loanAmount
        ),
        // send close loan instruction
        new TransactionInstruction({
          keys: [
            {
              pubkey: PROGRAM_STATE_ACCOUNT,
              isSigner: false,
              isWritable: false,
            },
            { pubkey: MARINADE_STATE_ID, isSigner: false, isWritable: true },
            { pubkey: props.loanAccount, isSigner: false, isWritable: true },
            {
              pubkey: program_vault_derived_pubKey,
              isSigner: false,
              isWritable: true,
            },
            { pubkey: owner, isSigner: false, isWritable: true },
            { pubkey: BANANA_MINT, isSigner: false, isWritable: true },
            {
              pubkey: bananaAtaAddress,
              isSigner: false,
              isWritable: true,
            },
            {
              pubkey: user_banana_transfer_authority.publicKey,
              isSigner: true,
              isWritable: false,
            },
            {
              pubkey: programVaultAuthority[0],
              isSigner: false,
              isWritable: false,
            },
            {
              pubkey: KONGS_HOARD,
              isSigner: false,
              isWritable: true,
            },
            {
              pubkey: MSOL_MINT,
              isSigner: false,
              isWritable: true,
            },
            {
              pubkey: SOL_LEG,
              isSigner: false,
              isWritable: true,
            },
            {
              pubkey: MSOL_LEG,
              isSigner: false,
              isWritable: true,
            },
            {
              pubkey: MSOL_TREASURY,
              isSigner: false,
              isWritable: true,
            },
            { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
            { pubkey: MARINADE_PROGRAM_ID, isSigner: false, isWritable: false },
          ],
          programId: RAD_ID,
          data,
        })
      )

      const walletBalance = await connection.getTokenAccountBalance(
        bananaAtaAddress
      )
      const walletBalanceBananas = walletBalance.value.uiAmount
      if (
        walletBalanceBananas !== null &&
        walletBalanceBananas < props.loanAmount / LAMPORTS_PER_SOL
      ) {
        throw Error(
          `Insufficient wallet balance of ${walletBalanceBananas.toFixed(
            9
          )} Bananas for transaction of ${
            props.loanAmount / LAMPORTS_PER_SOL
          } Bananas.`
        )
      }

      notify('info', 'Close Loan Request Approval Needed')
      const signers: Signer[] = [user_banana_transfer_authority]

      signature = await sendTransaction(transaction, connection, {
        signers: signers,
      })
      notify('info', 'Transaction sent:', signature)
      dispatch(setScanForNewLoans(true))

      await connection.confirmTransaction(signature, 'processed')
      notify('success', 'Transaction successful!', signature)
    } catch (error: any) {
      setLoading(false)
      dispatch(setScanForNewLoans(false))
      notify('error', `Transaction failed! ${error?.message}`, signature)
      return
    }
  }, [publicKey, props, connection, sendTransaction, dispatch, notify])

  return (
    <>
      {loading ? (
        <CircularProgress color="success" />
      ) : (
        <Button
          size={props.buttonSize}
          onClick={onClick}
          disabled={!publicKey}
          variant={'contained'}
        >
          {props.text ? props.text : 'Close Loan'}
        </Button>
      )}
    </>
  )
}
